"use client";

import { signIn } from "next-auth/react";
import { useState, useEffect } from "react";
import { submitAccessRequest } from "@/app/actions/admin";
import { sendOTP, isInvited } from "@/app/actions/otp";

type Mode = "login" | "request";
// PWA flow has a third step: enter the code received by email
type LoginStep = "email" | "code";

interface LoginClientProps {
  siteName: string;
  subtitle: string;
}

export default function LoginClient({ siteName, subtitle }: LoginClientProps) {
  const [mode, setMode] = useState<Mode>("login");
  const [isPwa, setIsPwa] = useState(false);

  // ── Sign in state ──────────────────────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [notInvited, setNotInvited] = useState(false); // "not on the guest list" state
  // PWA OTP state
  const [loginStep, setLoginStep] = useState<LoginStep>("email");
  const [code, setCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // ── Request access state ───────────────────────────────────────────────────
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqMessage, setReqMessage] = useState("");
  const [reqLoading, setReqLoading] = useState(false);
  const [reqSent, setReqSent] = useState(false);
  const [reqError, setReqError] = useState("");

  // Detect iOS PWA standalone mode + restore any in-progress OTP session
  useEffect(() => {
    const standalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
      || window.matchMedia("(display-mode: standalone)").matches;
    console.log("[login] PWA detection:", { standalone });
    setIsPwa(standalone);

    // Restore OTP flow state that survived a background/reload
    if (standalone) {
      try {
        const saved = sessionStorage.getItem("bnc_otp_flow");
        if (saved) {
          const { savedEmail, step, expiry } = JSON.parse(saved);
          // Only restore if the code hasn't expired yet (with 30s buffer)
          if (expiry && Date.now() < expiry - 30_000) {
            console.log("[login] restoring OTP flow for", savedEmail);
            setEmail(savedEmail);
            setLoginStep(step ?? "code");
          } else {
            console.log("[login] OTP flow expired, clearing");
            sessionStorage.removeItem("bnc_otp_flow");
          }
        }
      } catch (e) {
        console.warn("[login] could not restore OTP state", e);
      }
    }
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── PWA: send OTP code ─────────────────────────────────────────────────────
  async function handleSendOTP(targetEmail: string) {
    setLoading(true);
    setError("");
    setNotInvited(false);
    console.log("[login] sending OTP for", targetEmail);
    const result = await sendOTP(targetEmail);
    setLoading(false);
    if (!result.ok) {
      if (result.error === "NOT_INVITED") {
        setNotInvited(true);
        return false;
      }
      setError(result.error ?? "Failed to send code.");
      return false;
    }
    // Persist flow state so a background/reload doesn't lose the code step
    try {
      sessionStorage.setItem("bnc_otp_flow", JSON.stringify({
        savedEmail: targetEmail,
        step: "code",
        expiry: Date.now() + 10 * 60 * 1000, // 10 min matches server expiry
      }));
    } catch (e) {
      console.warn("[login] could not persist OTP state", e);
    }
    setResendCooldown(30);
    return true;
  }

  // ── Browser: send magic link ───────────────────────────────────────────────
  async function handleSendMagicLink(targetEmail: string) {
    setLoading(true);
    setError("");
    setNotInvited(false);
    console.log("[login] sending magic link for", targetEmail);

    // Guest-list pre-check FIRST. NextAuth's signIn() wraps a thrown
    // NOT_INVITED into an opaque error code, so we can't reliably detect it
    // from result.error — checking up front lets us show the Request Access
    // UI deterministically. (Backend gating in lib/auth.ts is unchanged.)
    const { invited } = await isInvited(targetEmail);
    if (!invited) {
      setLoading(false);
      setNotInvited(true);
      return false;
    }

    const result = await signIn("nodemailer", {
      email: targetEmail,
      redirect: false,
      callbackUrl: "/",
    });
    setLoading(false);
    if (result?.error) {
      // Defensive fallback — invited check passed but signIn still errored.
      if (result.error.includes("NOT_INVITED") || result.error === "EmailSignin") {
        setNotInvited(true);
        return false;
      }
      setError("Something went wrong. Please try again.");
      return false;
    }
    return true;
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;

    if (isPwa) {
      const ok = await handleSendOTP(email);
      if (ok) setLoginStep("code");
    } else {
      const ok = await handleSendMagicLink(email);
      if (ok) setSent(true);
    }
  }

  // ── PWA: verify the entered code ───────────────────────────────────────────
  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (!code || code.length !== 6) return;
    setCodeLoading(true);
    setCodeError("");
    console.log("[login] verifying OTP code for", email);

    try {
      // redirect:"follow" lets the browser follow the 302 from /api/otp-signin
      // so the Set-Cookie on the redirect response lands in the PWA's cookie jar
      const res = await fetch("/api/otp-signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
        redirect: "follow",
      });

      console.log("[login] OTP verify response:", res.status, res.url);

      // Error responses are JSON (4xx/5xx)
      if (!res.ok && res.status !== 302) {
        try {
          const data = await res.json();
          setCodeError(data.error ?? "Invalid code.");
        } catch {
          setCodeError("Something went wrong. Please try again.");
        }
        setCodeLoading(false);
        return;
      }

      // Success — redirect followed, cookie set, navigate to home
      try { sessionStorage.removeItem("bnc_otp_flow"); } catch { /* ignore */ }
      window.location.href = "/";
    } catch (err) {
      console.error("[login] OTP verify error:", err);
      setCodeError("Something went wrong. Please try again.");
      setCodeLoading(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0) return;
    await handleSendOTP(email);
  }

  async function handleRequestAccess(e: React.FormEvent) {
    e.preventDefault();
    if (!reqName || !reqEmail) return;
    setReqLoading(true);
    setReqError("");

    const fd = new FormData();
    fd.append("name", reqName);
    fd.append("email", reqEmail);
    fd.append("message", reqMessage);

    const result = await submitAccessRequest(fd);
    setReqLoading(false);

    if (result?.alreadyUser) {
      setEmail(reqEmail);
      setMode("login");
    } else if (result?.error) {
      setReqError(result.error);
    } else {
      setReqSent(true);
    }
  }

  function resetLogin() {
    setSent(false);
    setNotInvited(false);
    setLoginStep("email");
    setCode("");
    setCodeError("");
    setError("");
    try { sessionStorage.removeItem("bnc_otp_flow"); } catch { /* ignore */ }
  }

  // Switch to Request Access tab, pre-filling the email they already typed
  function goToRequestAccess() {
    setReqEmail(email);
    setNotInvited(false);
    setMode("request");
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <main className="min-h-screen bg-gradient-to-b from-purple-200 via-purple-100 to-pink-100 flex items-center justify-center px-6">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">🏆</div>
          <h1 className="text-3xl font-black text-purple-800 mb-2">
            {siteName}
          </h1>
          <p className="text-purple-600 text-sm font-semibold uppercase tracking-widest">
            {subtitle}
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur rounded-3xl shadow-2xl p-8">

          {/* ── Mode toggle tabs ────────────────────────────────────────────── */}
          {!sent && loginStep === "email" && !reqSent && !notInvited && (
            <div className="flex rounded-xl bg-purple-100 p-1 mb-6">
              <button
                onClick={() => setMode("login")}
                className={`flex-1 rounded-lg py-2 text-sm font-bold transition ${
                  mode === "login"
                    ? "bg-white text-purple-800 shadow"
                    : "text-purple-500 hover:text-purple-700"
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode("request")}
                className={`flex-1 rounded-lg py-2 text-sm font-bold transition ${
                  mode === "request"
                    ? "bg-white text-purple-800 shadow"
                    : "text-purple-500 hover:text-purple-700"
                }`}
              >
                Request Access
              </button>
            </div>
          )}

          {/* ── Sign In ──────────────────────────────────────────────────────── */}
          {mode === "login" && (
            <>
              {/* Not on the guest list */}
              {notInvited ? (
                <div className="text-center">
                  <div className="text-4xl mb-4">🚫</div>
                  <h2 className="text-xl font-black text-purple-800 mb-3">
                    You&apos;re not on the list
                  </h2>
                  <p className="text-purple-700 mb-2">
                    <span className="font-bold text-purple-900 bg-purple-100 rounded-lg px-2 py-0.5">{email}</span> doesn&apos;t have access yet.
                  </p>
                  <p className="text-sm text-purple-500 mb-6">
                    Ask the admins to invite you, or submit a request and they&apos;ll get you set up.
                  </p>
                  <button
                    onClick={goToRequestAccess}
                    className="w-full rounded-xl bg-purple-700 px-6 py-3 text-white font-bold text-lg shadow-lg hover:bg-purple-800 transition mb-3"
                  >
                    Request Access 🙋
                  </button>
                  <button
                    onClick={resetLogin}
                    className="text-sm text-purple-500 underline hover:text-purple-700"
                  >
                    Try a different email
                  </button>
                </div>

              /* Browser flow: sent magic link */
              ) : sent && !isPwa ? (
                <div className="text-center">
                  <div className="text-4xl mb-4">📬</div>
                  <h2 className="text-xl font-black text-purple-800 mb-3">
                    Check your inbox!
                  </h2>
                  <p className="text-purple-700 mb-2">
                    We sent a magic sign-in link to:
                  </p>
                  <p className="font-bold text-purple-900 bg-purple-100 rounded-xl px-4 py-2 mb-6">
                    {email}
                  </p>
                  <p className="text-sm text-purple-500">
                    The link expires in 24 hours. Check your spam folder if you
                    don&apos;t see it.
                  </p>
                  <button
                    onClick={resetLogin}
                    className="mt-6 text-sm text-purple-600 underline hover:text-purple-800"
                  >
                    Use a different email
                  </button>
                </div>

              /* PWA flow: enter the code */
              ) : loginStep === "code" && isPwa ? (
                <div>
                  <div className="text-center mb-6">
                    <div className="text-4xl mb-3">📱</div>
                    <h2 className="text-xl font-black text-purple-800 mb-2">
                      Enter your code
                    </h2>
                    <p className="text-purple-600 text-sm">
                      We sent a 6-digit code to:
                    </p>
                    <p className="font-bold text-purple-900 bg-purple-100 rounded-xl px-4 py-2 mt-2 mb-1">
                      {email}
                    </p>
                    <p className="text-xs text-purple-400">Expires in 10 minutes</p>
                  </div>

                  <form onSubmit={handleVerifyCode} className="space-y-4">
                    <div>
                      <label
                        htmlFor="otp-code"
                        className="block text-sm font-bold text-purple-700 mb-1 text-center"
                      >
                        6-Digit Code
                      </label>
                      <input
                        id="otp-code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        autoComplete="one-time-code"
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="000000"
                        className="w-full rounded-xl border-2 border-purple-200 px-4 py-4 text-purple-900 placeholder-purple-300 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200 transition text-center text-3xl font-mono tracking-widest"
                      />
                    </div>

                    {codeError && (
                      <p className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2 text-center">
                        {codeError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={codeLoading || code.length !== 6}
                      className="w-full rounded-xl bg-purple-700 px-6 py-3 text-white font-bold text-lg shadow-lg hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {codeLoading ? "Signing in…" : "Sign In ✨"}
                    </button>
                  </form>

                  <div className="mt-4 flex flex-col items-center gap-2">
                    <button
                      onClick={handleResend}
                      disabled={resendCooldown > 0}
                      className="text-sm text-purple-600 underline hover:text-purple-800 disabled:no-underline disabled:text-purple-400"
                    >
                      {resendCooldown > 0
                        ? `Resend code in ${resendCooldown}s`
                        : "Resend code"}
                    </button>
                    <button
                      onClick={resetLogin}
                      className="text-sm text-purple-400 hover:text-purple-600"
                    >
                      ← Use a different email
                    </button>
                  </div>
                </div>

              /* Default: email entry form */
              ) : (
                <>
                  <h2 className="text-xl font-black text-purple-800 mb-2">Sign In</h2>
                  <p className="text-purple-600 text-sm mb-6">
                    {isPwa
                      ? "Enter your email and we\u2019ll send you a sign-in code."
                      : "Enter your email and we\u2019ll send you a magic link. No password needed."}
                  </p>

                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-bold text-purple-700 mb-1"
                      >
                        Email Address
                      </label>
                      <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-xl border-2 border-purple-200 px-4 py-3 text-purple-900 placeholder-purple-300 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200 transition"
                      />
                    </div>

                    {error && (
                      <p className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={loading || !email}
                      className="w-full rounded-xl bg-purple-700 px-6 py-3 text-white font-bold text-lg shadow-lg hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {loading
                        ? (isPwa ? "Sending code…" : "Sending…")
                        : (isPwa ? "Send Code 📱" : "Send Magic Link ✨")}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          {/* ── Request Access ───────────────────────────────────────────────── */}
          {mode === "request" && (
            <>
              {reqSent ? (
                <div className="text-center">
                  <div className="text-4xl mb-4">🙋</div>
                  <h2 className="text-xl font-black text-purple-800 mb-3">
                    Request sent!
                  </h2>
                  <p className="text-purple-700 mb-2">
                    The admins have been notified. They&apos;ll send you an invite once you&apos;re approved.
                  </p>
                  <p className="text-sm text-purple-500 mt-4">
                    Keep an eye on <span className="font-bold">{reqEmail}</span> for your welcome email.
                  </p>
                  <button
                    onClick={() => { setReqSent(false); setReqName(""); setReqEmail(""); setReqMessage(""); }}
                    className="mt-6 text-sm text-purple-600 underline hover:text-purple-800"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-black text-purple-800 mb-2">Request Access</h2>
                  <p className="text-purple-600 text-sm mb-6">
                    Not on the list yet? Let the admins know who you are and they&apos;ll get you set up.
                  </p>

                  <form onSubmit={handleRequestAccess} className="space-y-4">
                    <div>
                      <label
                        htmlFor="req-name"
                        className="block text-sm font-bold text-purple-700 mb-1"
                      >
                        Your Name *
                      </label>
                      <input
                        id="req-name"
                        type="text"
                        required
                        value={reqName}
                        onChange={(e) => setReqName(e.target.value)}
                        placeholder="Grandma"
                        className="w-full rounded-xl border-2 border-purple-200 px-4 py-3 text-purple-900 placeholder-purple-300 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200 transition"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="req-email"
                        className="block text-sm font-bold text-purple-700 mb-1"
                      >
                        Email Address *
                      </label>
                      <input
                        id="req-email"
                        type="email"
                        required
                        value={reqEmail}
                        onChange={(e) => setReqEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-xl border-2 border-purple-200 px-4 py-3 text-purple-900 placeholder-purple-300 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200 transition"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="req-message"
                        className="block text-sm font-bold text-purple-700 mb-1"
                      >
                        Message <span className="font-normal text-purple-400">(optional)</span>
                      </label>
                      <textarea
                        id="req-message"
                        value={reqMessage}
                        onChange={(e) => setReqMessage(e.target.value)}
                        placeholder="I'm the proud grandparent and I have VERY strong opinions 👵"
                        rows={2}
                        className="w-full rounded-xl border-2 border-purple-200 px-4 py-3 text-purple-900 placeholder-purple-300 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200 transition resize-none"
                      />
                    </div>

                    {reqError && (
                      <p className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">
                        {reqError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={reqLoading || !reqName || !reqEmail}
                      className="w-full rounded-xl bg-purple-700 px-6 py-3 text-white font-bold text-lg shadow-lg hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {reqLoading ? "Sending..." : "Request Access 🙋"}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </main>
  );
}
